import { useMemo } from 'react'
import { usePageCharts } from '~/api/hooks/useCharts'
import type { ChartRegistryRow } from '~/api/endpoints/getCharts'

export interface DriverArms {
  /** Distinct driver fact tables this page+kpi binds (membership predicate input). */
  driverFacts: string[]
  /** The single driver kpi (only when exactly one driver fact) — for the tooltip label. */
  driverKpi?: string
}

/** Pure: derive the driver fact(s)/kpi from a page+kpi's chart_registry rows. FU-m only ever binds
 *  ONE driver (nsp / model_success_driver), so driverKpi resolves only in the single-fact case. */
export function deriveDriverBindings(rows: ChartRegistryRow[] | undefined): DriverArms {
  const facts = new Set<string>()
  const kpiByFact = new Map<string, string>()
  for (const r of rows ?? []) {
    const f = r.default_params?.['driver_fact']
    if (typeof f === 'string' && f.length > 0) {
      facts.add(f)
      const k = r.default_params?.['driver_kpi']
      if (typeof k === 'string' && k.length > 0) kpiByFact.set(f, k)
    }
  }
  const driverFacts = [...facts]
  const driverKpi = driverFacts.length === 1 ? kpiByFact.get(driverFacts[0]!) : undefined
  return { driverFacts, driverKpi }
}

/** Thin hook: the page already issued get_charts for PageTiles, so this is a react-query cache-hit. */
export function useDriverArms(pageId: string, kpi: string): DriverArms {
  const charts = usePageCharts(pageId, kpi)
  return useMemo(() => deriveDriverBindings(charts.data), [charts.data])
}
